import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { type Document } from '@/api/queries/storage';
import { DownloadButton } from './DownloadButton';
import { FilePreview } from './FilePreview';
import { useUserTimezone } from '@/hooks/useUserTimezone';

interface FileDetailViewProps {
  document: Document;
  onBack: () => void;
  formatFileSize: (bytes: number) => string;
}

export function FileDetailView({ document, onBack, formatFileSize }: FileDetailViewProps) {
  const { t: tStorage } = useTranslation('storage');
  const userTimezone = useUserTimezone();

  return (
    <div className="h-full bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between py-3 px-6 border border-gray-200 rounded-t-lg">
        <div className="flex flex-col items-start">
          <Button variant="ghost" onClick={onBack} className="p-0 h-auto font-medium">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {tStorage('detail.backToFiles')}
          </Button>
          <h1 className="text-2xl font-semibold text-gray-900">{document.name}</h1>
        </div>

        <div className="flex items-center gap-3">
          <DownloadButton
            selectedDocuments={[document.id]}
            documents={[document]}
            data-testid="file-detail-download-button"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex min-h-0 border border-gray-200 rounded-b-lg">
        {/* File Preview Area */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-auto">
            <FilePreview document={document} />
          </div>
        </div>

        {/* Details Sidebar */}
        <div className="w-80 border-l border-gray-200">
          <div className="p-6">
            <div className="space-y-6">
              {/* Details Tab */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {tStorage('detail.details')}
                </h3>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">{tStorage('detail.size')}</h4>
                    <div className="space-y-2 text-sm text-gray-600 mb-2">
                      {formatFileSize(document.size)}
                    </div>
                    <h4 className="text-sm font-medium text-gray-700">{tStorage('detail.type')}</h4>
                    <div className="space-y-2 text-sm text-gray-600 mb-2">{document.file_type}</div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">
                      {tStorage('detail.lastModified')}
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="text-gray-600">
                        <Trans
                          i18nKey="detail.lastModifiedAtBy"
                          values={{
                            date: new Date(document.last_modified_date),
                            user: document.last_modified_by || tStorage('detail.unknownUser'),
                            formatParams: {
                              date: {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric',
                                timeZone: userTimezone,
                              },
                            },
                          }}
                          components={[<span className="font-medium" />]}
                          t={tStorage}
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">
                      {tStorage('detail.uploaded')}
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="text-gray-600">
                        <Trans
                          i18nKey="detail.uploadedAtBy"
                          values={{
                            date: new Date(document.created_date),
                            user: document.created_by || tStorage('detail.unknownUser'),
                            formatParams: {
                              date: {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric',
                                timeZone: userTimezone,
                              },
                            },
                          }}
                          components={[<span className="font-medium" />]}
                          t={tStorage}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
